import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { useEffect } from 'react';
import styled from 'styled-components';
import Modal from '@/components/modal/modal-component';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: 20px;
  background-color: ${({ theme }) => theme.COLOR_BG_LIGHT};
  gap: 10px;
`;

const ContentContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.COLOR_TEXT_SECONDARY};
  font-size: 14px;
  margin: 0;
`;

/**
 * ParentSelectionModal Component
 *
 * Modal for selecting which historical option should be the parent when multiple
 * options exist with parent_option: null. This handles the migration of historical
 * data to the new parent-variant structure.
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onConfirm - Function called when parent is selected
 * @param {Array} props.options - Array of options with parent_option: null
 * @param {boolean} props.loading - Whether the operation is loading
 * @returns {JSX.Element} The rendered parent selection modal
 */
const ParentSelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  options = [],
  loading = false,
}) => {
  const methods = useForm({
    defaultValues: {
      parentOptionId: '',
    },
  });

  const { handleSubmit, reset, watch } = methods;
  const selectedParentId = watch('parentOptionId');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      reset({
        parentOptionId: '',
      });
    }
  }, [isOpen, reset]);

  // Create dropdown options from the historical data
  const dropdownOptions = options.map((option) => ({
    value: option.id,
    label: `${option.description || 'No Description'} (${
      option.part_number || 'No Part Number'
    })`,
  }));

  const handleFormSubmit = (values) => {
    const selectedOption = options.find(
      (option) => option.id === values.parentOptionId,
    );

    if (selectedOption) {
      onConfirm(selectedOption);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      showClose
      title="Select Parent Option"
      close={handleClose}
      width="600px"
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0px',
      }}
    >
      <FormProvider {...methods}>
        <ContentContainer>
          <InfoText>
            Select the main option. All others will become its variants.
          </InfoText>

          <SelectSearch
            cy="selParentOption"
            header="Parent Option"
            name="parentOptionId"
            label="Please select a parent option"
            options={dropdownOptions}
            required={{
              required: 'Please select a parent option',
            }}
            isLoading={loading}
          />
        </ContentContainer>
      </FormProvider>

      <ButtonContainer>
        <PrimaryOutlineButton
          buttonWidth="108px"
          type="button"
          loading={false}
          title="Cancel"
          onClick={handleClose}
          margin="0 0 0 0"
        />
        <PrimaryButton
          buttonWidth="108px"
          type="submit"
          loading={loading}
          title="Confirm"
          onClick={handleSubmit(handleFormSubmit)}
          disabled={!selectedParentId}
        />
      </ButtonContainer>
    </Modal>
  );
};

ParentSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      description: PropTypes.string,
      part_number: PropTypes.string,
      price_id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  loading: PropTypes.bool.isRequired,
};

export default ParentSelectionModal;
